import { AsyncPipe, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  allSinisterContexts,
  InsuranceSuggestion,
  HTMLElementEvent,
} from '@sc/data-access-models';
import { SandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  IconComponent,
  InputHourComponent,
  InputTextComponent,
  MomentParseZonePipe,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { Subscription } from 'rxjs';
@Component({
  selector: 'sc-planning-form-appointment-insurance',
  styleUrls: ['./planning-form-appointment-insurance.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatRadioModule,
    NgFor,
    NgIf,
    InputTextComponent,
    MatInputModule,
    MatDatepickerModule,
    IconComponent,
    InputHourComponent,
    AsyncPipe,
    TitleCasePipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <ng-container [formGroup]="form">
      <fieldset>
        <legend>
          {{ 'PlanningFormAppointmentInsurance.title' | translate }}
        </legend>
        <ng-container formGroupName="claim">
          <div class="form-row" formGroupName="car">
            <div class="form-data" formGroupName="ownerShip">
              <mat-label class="title text-grey-dark">{{
                'PlanningFormAppointmentInsurance.ownerShipTypeLabel'
                  | translate
              }}</mat-label>
              <mat-radio-group formControlName="type">
                <mat-radio-button
                  *ngFor="let ownerShipType of ownerShipTypes"
                  [value]="ownerShipType"
                  [checked]="
                    form
                      ?.get('claim')
                      ?.get('car')
                      ?.get('ownerShip')
                      ?.get('type')?.value === ownerShipType
                  "
                >
                  <span class="sc-design-text-3">{{
                    'GlobalApplicativeConstants.ownerShipType.' + ownerShipType
                      | translate
                  }}</span></mat-radio-button
                >
              </mat-radio-group>
            </div>
          </div>
          <div
            class="form-row grey-bg"
            formGroupName="car"
            *ngIf="
              form?.get('claim')?.get('car')?.get('ownerShip')?.get('type')
                ?.value === 'businesslike'
            "
          >
            <div class="form-data" formGroupName="ownerShip">
              <sc-input-text
                [label]="
                  'PlanningFormAppointmentInsurance.ownershipLabel' | translate
                "
                formControlName="name"
              ></sc-input-text>
            </div>
          </div>
        </ng-container>
        <div class="form-row" formGroupName="policyInformation">
          <div class="form-data">
            <mat-label class="title text-grey-dark">{{
              'PlanningFormAppointmentInsurance.insuranceChoiceLabel'
                | translate
            }}</mat-label>
            <mat-radio-group formControlName="insuranceChoice">
              <mat-radio-button [value]="'true'"
                ><span class="sc-design-text-3">{{
                  'GlobalApplicativeConstants.action.yes' | translate
                }}</span>
              </mat-radio-button>
              <mat-radio-button [value]="'false'"
                ><span class="sc-design-text-3">{{
                  'GlobalApplicativeConstants.action.no' | translate
                }}</span>
              </mat-radio-button>
              <mat-radio-button [value]="'unknown'"
                ><span class="sc-design-text-3">{{
                  'GlobalApplicativeConstants.action.unknown' | translate
                }}</span>
              </mat-radio-button>
            </mat-radio-group>
          </div>
        </div>
        <div class="grey-bg">
          <div class="form-row" formGroupName="policyInformation">
            <div class="form-data">
              <sc-input-text
                id="insuranceName"
                [label]="
                  'PlanningFormAppointmentInsurance.insuranceNameLabel'
                    | translate
                "
                formControlName="insuranceName"
                [suggestions]="filteredInsuranceSuggestions"
                (input)="onInsuranceNameInputChange($event)"
                (selectSuggestion)="onSelectInsuranceName($event)"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <sc-input-text
                [label]="
                  'PlanningFormAppointmentInsurance.constractNumberLabel'
                    | translate
                "
                formControlName="policyNumber"
              ></sc-input-text>
            </div>
          </div>
          <div class="form-row" formGroupName="policyInformation">
            <div class="form-data">
              <sc-input-text
                [label]="
                  'PlanningFormAppointmentInsurance.excessLabel' | translate
                "
                formControlName="excess"
              ></sc-input-text>
            </div>
            <div class="form-data">
              <sc-input-text
                [label]="
                  'PlanningFormAppointmentInsurance.sinisterNumberLabel'
                    | translate
                "
                formControlName="sinisterNumber"
              ></sc-input-text>
            </div>
          </div>
          <div class="form-row" formGroupName="claim">
            <div class="form-data">
              <mat-label class="sc-design-text-4"
                ><label id="sinister-date-label">{{
                  'PlanningFormAppointmentInsurance.dateLabel' | translate
                }}</label></mat-label
              >
              <mat-form-field>
                <input
                  matInput
                  (click)="inputDatePicker?.click()"
                  [value]="
                    form.get('claim')?.get('sinisterDate')?.value !== null
                      ? (getDateFormatted()
                        | parseZoneTimeString
                          : timezoneService.getTimezone(
                              form.get('appointment')?.value
                            )
                        | localeDate: 'fullDate'
                        | async
                        | titlecase)
                      : ''
                  "
                  readonly
                  aria-labelledby="sinister-date-label"
                  [disabled]="form?.disabled"
                />

                <input
                  class="hidden-input"
                  #inputDatePicker
                  matInput
                  [max]="today"
                  [matDatepicker]="picker"
                  (focus)="picker.open()"
                  (click)="picker.open()"
                  (dateChange)="onDateChange($event)"
                  readonly
                  [disabled]="form?.disabled"
                />
                <sc-icon matSuffix name="arrow_bottom"></sc-icon>

                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>
            </div>
            <div class="form-data hour-field">
              <sc-input-hour
                formControlName="sinisterDate"
                [label]="
                  'PlanningFormAppointmentInsurance.hourLabel' | translate
                "
                [timezone]="
                  timezoneService.getTimezone(form.get('appointment')?.value)
                "
              ></sc-input-hour>
            </div>
          </div>
          <ng-container formGroupName="claim">
            <div class="form-row">
              <div class="form-data">
                <sc-input-text
                  [label]="
                    'PlanningFormAppointmentInsurance.sinisterLocationLabel'
                      | translate
                  "
                  formControlName="sinisterLocation"
                ></sc-input-text>
              </div>
            </div>

            <div class="form-row">
              <div class="form-data">
                <sc-input-text
                  [suggestions]="sinisterContextList"
                  [label]="
                    'PlanningFormAppointmentInsurance.sinisterContextLabel'
                      | translate
                  "
                  [value]="
                    form.get('claim').get('sinisterContext').get('value')?.value
                  "
                  (selectSuggestion)="onSelectSinisterContext($event)"
                  (updateSuggestions)="onSinisterContextChange($event)"
                  [disabled]="form?.disabled"
                  [updateSuggestionDebounceTime]="350"
                ></sc-input-text>
              </div>
            </div>
          </ng-container>
        </div>
      </fieldset>
    </ng-container>
  `,
})
export class PlanningFormAppointmentInsuranceComponent
  implements OnInit, OnDestroy
{
  @Input() form: FormGroup;
  @Input() set touched(value) {
    if (value) {
      this.form?.get('claim').markAllAsTouched();
      this.form?.get('policyInformation').markAllAsTouched();
    }
  }

  today = new Date();
  ownerShipTypes;
  insuranceSuggestions: InsuranceSuggestion[];
  filteredInsuranceSuggestions: string[] = [];
  sinisterContextList: string[] = allSinisterContexts.map((context) =>
    this.translator.getInstantTranslation(context.value)
  );

  private readonly subs = new Subscription();

  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly translator: TranslatorService,
    public timezoneService: TimezoneService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly sandboxSB: SandboxService,
    public featureFlippingService: FeatureFlippingManagerService
  ) {}

  ngOnInit() {
    this.initOwnerShipTypes();
    this.initInsuranceNameSuggestions();

    const sub = this.form.valueChanges.subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
    this.subs.add(sub);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  initOwnerShipTypes() {
    this.ownerShipTypes =
      this.configurationService.getConfig()?.carOwnerShipTypes?.carOwnerShipTypesList;
  }

  getStart() {
    return this.form.get('claim').get('sinisterDate') as FormControl;
  }

  getDateFormatted() {
    return dayjs
      .utc(this.form.get('claim').get('sinisterDate')?.value)
      .tz(this.timezoneService.getTimezone(this.form.get('appointment')?.value))
      .toISOString();
  }

  onDateChange($event) {
    const oldSinisterDate = dayjs
      .utc(this.form.get('claim').get('sinisterDate')?.value)
      .tz(
        this.timezoneService.getTimezone(this.form.get('appointment')?.value)
      );

    const oldSinisterHour = oldSinisterDate?.hour() || 0;
    const oldSinisterMinutes = oldSinisterDate?.minute() || 0;

    const newSinisterDate = dayjs
      .utc($event?.value)
      ?.tz(
        this.timezoneService.getTimezone(this.form.get('appointment')?.value)
      )
      ?.clone()
      ?.hour(oldSinisterHour)
      ?.minute(oldSinisterMinutes)
      ?.second(0)
      ?.format();

    this.form.get('claim').get('sinisterDate')?.setValue(newSinisterDate);
  }

  onHourSelection(value) {
    let time;
    if (value) {
      const splitValue = value.split(':');

      const hours = +splitValue[0].replace(/\D/g, '');
      const minutes = +splitValue[1].replace(/\D/g, '');

      time = dayjs
        .utc(this.getStart()?.value)
        ?.clone()
        ?.tz(
          this.timezoneService.getTimezone(this.form.get('appointment')?.value)
        )
        ?.hour(hours)
        ?.minute(minutes)
        ?.toISOString();
      this.form.get('claim').get('sinisterDate')?.setValue(time);
    }
  }

  initInsuranceNameSuggestions() {
    if (
      this.featureFlippingService.isFeatureActivated(
        'insurance-suggestion-from-iclist'
      )
    ) {
      this.sandboxSB.getICList().subscribe((liste) => {
        this.insuranceSuggestions = liste;
        this.filteredInsuranceSuggestions = this.insuranceSuggestions.map(
          (insuranceChoice) => insuranceChoice.insuranceName
        );
      });
    } else {
      this.insuranceSuggestions =
        this.configurationService.getConfig()?.insuranceSuggestion;
      this.filteredInsuranceSuggestions = this.insuranceSuggestions.map(
        (insuranceChoice) => insuranceChoice.insuranceName
      );
    }
  }

  onSelectSinisterContext($event) {
    const context = allSinisterContexts?.find(
      (item) => this.translator.getInstantTranslation(item.value) === $event
    );
    if (context?.key) {
      this.form
        .get('claim')
        ?.get('sinisterContext')
        ?.get('key')
        ?.setValue(context.key);
      this.form
        .get('claim')
        ?.get('sinisterContext')
        ?.get('value')
        ?.setValue($event);
    }
  }

  onSinisterContextChange($event) {
    const context = allSinisterContexts?.find(
      (item) => this.translator.getInstantTranslation(item.value) === $event
    );
    const formContext = this.form.get('claim')?.get('sinisterContext')?.value;
    if (context?.key && context?.key !== formContext?.key) {
      this.form
        .get('claim')
        ?.get('sinisterContext')
        ?.get('key')
        ?.setValue(context?.key);
    } else if (!context?.key) {
      this.form.get('claim')?.get('sinisterContext')?.get('key')?.setValue('O');
    }

    this.form
      .get('claim')
      ?.get('sinisterContext')
      ?.get('value')
      ?.setValue($event);
  }

  onInsuranceNameInputChange(event: Event) {
    const value = (event as HTMLElementEvent<HTMLInputElement>)?.target?.value;

    this.filteredInsuranceSuggestions =
      this.configurationService.filterInsuranceSuggestions(
        this.insuranceSuggestions,
        value
      );
    this.form.get('policyInformation').get('insuranceCode')?.setValue(null); // remove insuranceCode if none of the suggested insurances has been selected
  }

  onSelectInsuranceName($event) {
    this.form?.get('policyInformation').get('insuranceName')?.setValue($event);
    this.form
      ?.get('policyInformation')
      .get('insuranceCode')
      ?.setValue(
        this.insuranceSuggestions.filter(
          (suggestion) => suggestion.insuranceName === $event
        )[0]?.id
      );
  }
}
